/**
 * The Wishlist drawer (snippets/pag-wishlist-panel.liquid): header count
 * bubble, and hydrating the panel's product grid the first time it opens.
 *
 * Count is read straight from device storage on load — no request, no
 * flicker to a number then a different number — and kept in sync via
 * pag-personal-store's change event, which already fires on this tab's own
 * saves/removes and on a `storage` event from another tab, so the count
 * (and, if the panel is open, the grid) agree across tabs for free.
 */

import { saved, CHANGE_EVENT } from '@theme/pag-personal-store';
import { fetchProductCards, announceHydrated } from '@theme/pag-product-hydrate';
import { StandardEvents } from '@shopify/events';

const PANEL_ID = 'pag-wishlist-panel';
const COUNT_SELECTOR = '[data-pag-wishlist-count]';

function syncCount() {
  const count = saved.size;
  document.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
    el.textContent = String(count);
    el.hidden = count === 0;
  });

  const panel = document.getElementById(PANEL_ID);
  const panelCount = panel?.querySelector('[data-pag-panel-count]');
  if (panelCount) {
    panelCount.textContent = `(${count})`;
    panelCount.hidden = count === 0;
  }
}

async function fetchWithRetry(ids) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchProductCards(ids);
    } catch (error) {
      if (attempt === 2) throw error;
      await new Promise((resolve) => setTimeout(resolve, 600 * (attempt + 1)));
    }
  }
}

// A failed load must not read as "empty": show a retry instead.
function showError(list, empty, viewAll) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pag-wishlist-panel__browse button-unstyled';
  button.textContent = "Couldn't load your saved items. Tap to retry.";
  button.addEventListener('click', hydrate);
  const item = document.createElement('li');
  item.append(button);
  list.replaceChildren(item);
  list.hidden = false;
  if (empty) empty.hidden = true;
  if (viewAll) viewAll.hidden = true;
}

async function hydrate() {
  const panel = document.getElementById(PANEL_ID);
  if (!panel) return;

  const list = panel.querySelector('[data-pag-wishlist-list]');
  const empty = panel.querySelector('[data-pag-wishlist-empty]');
  const viewAll = panel.querySelector('[data-pag-wishlist-viewall]');
  if (!list) return;

  const ids = saved.all().map((entry) => entry.id);

  const showEmpty = () => {
    list.hidden = true;
    list.replaceChildren();
    if (empty) empty.hidden = false;
    if (viewAll) viewAll.hidden = true;
  };

  if (ids.length === 0) {
    showEmpty();
    return;
  }

  let cards;
  try {
    cards = await fetchWithRetry(ids);
  } catch {
    showError(list, empty, viewAll);
    return;
  }

  if (cards.length === 0) {
    showEmpty();
    return;
  }

  list.replaceChildren(
    ...cards.map((card) => {
      // Compact: the panel is a narrow column, two cards wide — see the
      // matching two-up layout the search drawer mockup used for the same
      // reason. pag-mini-card's own save button is what removes an item
      // from here; no separate control needed.
      card.classList.add('pag-mini-card--compact');
      const item = document.createElement('li');
      item.className = 'pag-wishlist-panel__item';
      item.appendChild(card);
      return item;
    })
  );
  list.hidden = false;
  if (empty) empty.hidden = true;
  if (viewAll) viewAll.hidden = false;
  announceHydrated(panel);
}

document.addEventListener('pag:panel:open', (event) => {
  if (event.target instanceof Element && event.target.id === PANEL_ID) hydrate();
});

document.addEventListener(CHANGE_EVENT, (event) => {
  if (event.detail?.list !== 'saved') return;
  syncCount();
  const panel = document.getElementById(PANEL_ID);
  if (panel instanceof HTMLDialogElement && panel.open) hydrate();
});

syncCount();

// "Move to Cart": the card's own add form does the add; once the cart reports a change
// for a tap made here, the product leaves the wishlist.
let pendingMove = null;

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('.pag-wishlist-panel__item .pag-mini-card__move button') : null;
  pendingMove = trigger?.closest('[data-product-id]')?.dataset.productId ?? null;
});

document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  if (!pendingMove) return;
  const id = pendingMove;
  pendingMove = null;
  saved.remove(id);
});
